import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  createFormQuestionFromLibrary,
  createTrackerSpecificFormQuestion,
  FORM_QUESTION_SOURCES,
  FORM_ROLE_OPTIONS,
  getEffectiveFormQuestions,
  isFirstSubmissionForm,
  isPersonalInfoLibraryId,
  normalizeFormQuestion,
} from "./trackerStorage";
import {
  enrichQuestionFromLibrary,
  getFieldTypeLabel,
  getMeasureTypeLabel,
  getMeasureTypeMeta,
  getQuestionModeLabel,
  getQuestions,
  isScaleFieldType,
  MEASURE_TYPES,
  needsOptions,
  QUESTION_FIELD_TYPES,
  QUESTION_MODES,
} from "./questions/questionStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const PreviewShell = ({ children, sx = {} }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "8px",
      px: 1.25,
      py: 1.15,
      color: MUTED,
      fontSize: 13,
      bgcolor: "#fff",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const PreviewSelect = ({ placeholder, options = [] }) => (
  <PreviewShell
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <span>{placeholder || "Select an option"}</span>
    <KeyboardArrowDownIcon sx={{ fontSize: 18, color: MUTED }} />
  </PreviewShell>
);

const resolvePreviewQuestion = (question, libraryById) => {
  if (!question) return question;
  const lib =
    (question.questionId && libraryById.get(question.questionId)) || null;
  return enrichQuestionFromLibrary(question, lib);
};

const PreviewField = ({ question }) => {
  const label = question.label || "Untitled question";
  const requiredMark = question.required ? " *" : "";
  const fieldType = question.fieldType || "short_text";
  const isMeasurable =
    question.measurable || question.mode === QUESTION_MODES.MEASURABLE;
  const measureMeta = getMeasureTypeMeta(question.measureType);
  const unit =
    question.defaultUnit ||
    (Array.isArray(question.units) && question.units[0]) ||
    (measureMeta?.units && measureMeta.units[0]) ||
    "";
  const scaleMin = question.scaleMin ?? 1;
  const scaleMax = question.scaleMax ?? 10;
  const placeholder =
    question.placeholder ||
    (isScaleFieldType(fieldType)
      ? `Rate from ${scaleMin} to ${scaleMax}`
      : ["dropdown", "multiple_choice", "status", "coach", "gym", "programme", "member"].includes(
          fieldType
        )
        ? "Select an option"
        : isMeasurable || fieldType === "number"
          ? unit
            ? `Enter value (${unit})`
            : "Enter number"
          : fieldType === "email"
            ? "name@example.com"
            : fieldType === "phone"
              ? "Enter phone number"
              : fieldType === "long_text"
                ? "Write your answer…"
                : "");

  const selectTypes = [
    "dropdown",
    "status",
    "coach",
    "gym",
    "programme",
    "member",
  ];

  let control = null;

  if (fieldType === "long_text") {
    control = (
      <PreviewShell sx={{ minHeight: 88, py: 1, alignItems: "flex-start" }}>
        {placeholder || " "}
      </PreviewShell>
    );
  } else if (fieldType === "yes_no") {
    control = (
      <Box sx={{ display: "flex", gap: 1 }}>
        {["Yes", "No"].map((option) => (
          <PreviewShell
            key={option}
            sx={{
              flex: 1,
              textAlign: "center",
              py: 1,
              color: "#334155",
            }}
          >
            {option}
          </PreviewShell>
        ))}
      </Box>
    );
  } else if (fieldType === "checkbox") {
    control = (
      <Box>
        {(question.options || ["Option 1", "Option 2"]).map((option) => (
          <Box
            key={option}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.75,
              fontSize: 13,
              color: "#334155",
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                border: `1px solid ${BORDER}`,
                borderRadius: "4px",
                flexShrink: 0,
              }}
            />
            {option}
          </Box>
        ))}
      </Box>
    );
  } else if (fieldType === "multiple_choice") {
    control = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {(question.options || ["Option 1", "Option 2"]).map((option) => (
          <PreviewShell
            key={option}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#334155",
              py: 1,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                border: `1px solid ${BORDER}`,
                borderRadius: "50%",
                flexShrink: 0,
              }}
            />
            {option}
          </PreviewShell>
        ))}
      </Box>
    );
  } else if (selectTypes.includes(fieldType)) {
    control = <PreviewSelect placeholder={placeholder} />;
  } else if (isScaleFieldType(fieldType)) {
    control = (
      <PreviewSelect
        placeholder={placeholder || `Rate from ${scaleMin} to ${scaleMax}`}
        options={
          Array.isArray(question.options) && question.options.length
            ? question.options
            : []
        }
      />
    );
  } else if (fieldType === "number" || isMeasurable) {
    control = (
      <PreviewShell
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <span>{placeholder || "0"}</span>
        {unit ? (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: "6px",
              bgcolor: "#f8fafc",
              border: `1px solid ${BORDER}`,
              color: "#475569",
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {unit}
          </Box>
        ) : null}
      </PreviewShell>
    );
  } else if (
    fieldType === "date" ||
    fieldType === "review_date" ||
    fieldType === "follow_up_date"
  ) {
    control = (
      <PreviewShell
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{placeholder || "Select date"}</span>
        <Typography sx={{ fontSize: 12, color: MUTED }}>DD/MM/YYYY</Typography>
      </PreviewShell>
    );
  } else if (fieldType === "time") {
    control = (
      <PreviewShell
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{placeholder || "Select time"}</span>
        <Typography sx={{ fontSize: 12, color: MUTED }}>HH:MM</Typography>
      </PreviewShell>
    );
  } else if (fieldType === "image_upload" || fieldType === "file_upload") {
    control = (
      <PreviewShell
        sx={{
          minHeight: 88,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderStyle: "dashed",
          textAlign: "center",
          color: MUTED,
        }}
      >
        {fieldType === "image_upload"
          ? "Tap to upload an image"
          : "Tap to upload a file"}
      </PreviewShell>
    );
  } else if (fieldType === "email" || fieldType === "phone") {
    control = <PreviewShell>{placeholder || " "}</PreviewShell>;
  } else {
    control = <PreviewShell>{placeholder || " "}</PreviewShell>;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: "#0f172a",
          mb: 1,
        }}
      >
        {label}
        {requiredMark}
      </Typography>
      {question.helpText ? (
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>
          {question.helpText}
        </Typography>
      ) : null}
      {control}
    </Box>
  );
};

export default function EditFormFieldsDialog({
  open,
  form,
  sharedQuestions = [],
  personalInfoQuestions = [],
  allForms = [],
  trackerName,
  onClose,
  onChange,
}) {
  const [draft, setDraft] = useState({
    name: "",
    role: "first_submission",
    shortDescription: "",
    questions: [],
    excludedSharedQuestionIds: [],
    sharedOverrides: {},
  });
  const [advancedOpen, setAdvancedOpen] = useState({});
  const [pickLibraryOpen, setPickLibraryOpen] = useState(false);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [libraryTab, setLibraryTab] = useState("all"); // all | general | measurable
  const [librarySearch, setLibrarySearch] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const libraryQuestions = useMemo(
    () => getQuestions(),
    [open, pickLibraryOpen]
  );

  useEffect(() => {
    if (!open || !form) return;
    setDraft({
      name: form.name || "",
      role: form.role || "first_submission",
      shortDescription: form.shortDescription || "",
      questions: (form.questions || [])
        .map((q, index) => normalizeFormQuestion(q, index))
        .filter(Boolean),
      excludedSharedQuestionIds: Array.isArray(form.excludedSharedQuestionIds)
        ? [...form.excludedSharedQuestionIds]
        : [],
      sharedOverrides:
        form.sharedOverrides && typeof form.sharedOverrides === "object"
          ? { ...form.sharedOverrides }
          : {},
    });
    setAdvancedOpen({});
    setPickLibraryOpen(false);
    setSelectedLibraryIds([]);
    setLibraryTab("all");
    setLibrarySearch("");
  }, [open, form?.id]);

  const pushChange = (next) => {
    setDraft(next);
    onChange?.({
      name: next.name,
      role: next.role,
      shortDescription: next.shortDescription,
      excludedSharedQuestionIds: next.excludedSharedQuestionIds || [],
      sharedOverrides: next.sharedOverrides || {},
      questions: next.questions.map((item, order) =>
        normalizeFormQuestion({ ...item, order }, order)
      ),
    });
  };

  const updateMeta = (patch) => {
    pushChange({ ...draft, ...patch });
  };

  const isSharedExcluded = (sharedId) =>
    (draft.excludedSharedQuestionIds || []).includes(sharedId);

  const setSharedIncluded = (sharedId, included) => {
    const current = new Set(draft.excludedSharedQuestionIds || []);
    if (included) current.delete(sharedId);
    else current.add(sharedId);
    pushChange({
      ...draft,
      excludedSharedQuestionIds: Array.from(current),
    });
  };

  const updateSharedOverride = (sharedId, patch) => {
    pushChange({
      ...draft,
      sharedOverrides: {
        ...(draft.sharedOverrides || {}),
        [sharedId]: {
          ...(draft.sharedOverrides?.[sharedId] || {}),
          ...patch,
        },
      },
    });
  };

  const resolveSharedQuestion = (sharedQ) => {
    const override = draft.sharedOverrides?.[sharedQ.id] || {};
    return {
      ...sharedQ,
      ...override,
      source: FORM_QUESTION_SOURCES.SHARED,
      sharedQuestionId: sharedQ.id,
    };
  };

  const updateQuestion = (id, patch) => {
    pushChange({
      ...draft,
      questions: draft.questions.map((item) =>
        item.id === id ? { ...item, ...patch } : item
      ),
    });
  };

  const reorderQuestion = (fromIndex, toIndex) => {
    if (
      fromIndex == null ||
      toIndex == null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= draft.questions.length ||
      toIndex >= draft.questions.length
    ) {
      return;
    }
    const next = [...draft.questions];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    pushChange({
      ...draft,
      questions: next.map((item, order) => ({ ...item, order })),
    });
  };

  const removeQuestion = (id) => {
    pushChange({
      ...draft,
      questions: draft.questions
        .filter((item) => item.id !== id)
        .map((item, order) => ({ ...item, order })),
    });
  };

  const addBlankField = () => {
    const question = createTrackerSpecificFormQuestion(
      {
        label: "",
        placeholder: "",
        fieldType: "short_text",
        required: false,
      },
      draft.questions.length
    );
    pushChange({
      ...draft,
      questions: [...draft.questions, question],
    });
    setAdvancedOpen((prev) => ({ ...prev, [question.id]: true }));
  };

  const isFirstForm = isFirstSubmissionForm(
    { ...draft, id: form?.id },
    allForms.length ? allForms : [{ ...draft, id: form?.id }]
  );

  const sharedLibraryIds = useMemo(
    () =>
      new Set(
        (sharedQuestions || []).map((q) => q.questionId).filter(Boolean)
      ),
    [sharedQuestions]
  );

  const alreadyLinkedLibraryIds = useMemo(
    () =>
      new Set([
        ...Array.from(sharedLibraryIds),
        ...(personalInfoQuestions || [])
          .map((q) => q.questionId)
          .filter(Boolean),
        ...draft.questions
          .filter(
            (q) => q.source === FORM_QUESTION_SOURCES.LIBRARY && q.questionId
          )
          .map((q) => q.questionId),
      ]),
    [draft.questions, sharedLibraryIds, personalInfoQuestions]
  );

  const availableLibrary = libraryQuestions.filter(
    (item) => !alreadyLinkedLibraryIds.has(item.id)
  );

  const libraryCounts = useMemo(() => {
    const general = availableLibrary.filter(
      (item) => item.mode !== QUESTION_MODES.MEASURABLE
    ).length;
    const measurable = availableLibrary.filter(
      (item) => item.mode === QUESTION_MODES.MEASURABLE
    ).length;
    return {
      all: availableLibrary.length,
      general,
      measurable,
    };
  }, [availableLibrary]);

  const filteredLibrary = useMemo(() => {
    const q = librarySearch.trim().toLowerCase();
    return availableLibrary.filter((item) => {
      if (isPersonalInfoLibraryId(item.id)) return false;
      if (libraryTab === "general" && item.mode === QUESTION_MODES.MEASURABLE) {
        return false;
      }
      if (
        libraryTab === "measurable" &&
        item.mode !== QUESTION_MODES.MEASURABLE
      ) {
        return false;
      }
      if (!q) return true;
      const typeLabel =
        item.mode === QUESTION_MODES.MEASURABLE
          ? getMeasureTypeLabel(item.measureType)
          : getFieldTypeLabel(item.fieldType);
      return `${item.label || ""} ${typeLabel}`.toLowerCase().includes(q);
    });
  }, [availableLibrary, libraryTab, librarySearch]);

  const selectAllLibraryShown = () => {
    const shownIds = filteredLibrary.map((item) => item.id);
    setSelectedLibraryIds((prev) => Array.from(new Set([...prev, ...shownIds])));
  };

  const confirmLibraryPicks = () => {
    const toAdd = libraryQuestions
      .filter((item) => selectedLibraryIds.includes(item.id))
      .map((item, index) =>
        createFormQuestionFromLibrary(item, draft.questions.length + index)
      );
    pushChange({
      ...draft,
      questions: [...draft.questions, ...toAdd],
    });
    setSelectedLibraryIds([]);
    setPickLibraryOpen(false);
  };

  const updateOption = (questionId, optionIndex, value) => {
    const question = draft.questions.find((item) => item.id === questionId);
    if (!question) return;
    const options = [...(question.options || [])];
    options[optionIndex] = value;
    updateQuestion(questionId, { options });
  };

  const addOption = (questionId) => {
    const question = draft.questions.find((item) => item.id === questionId);
    if (!question) return;
    updateQuestion(questionId, {
      options: [...(question.options || []), `Option ${(question.options || []).length + 1}`],
    });
  };

  const removeOption = (questionId, optionIndex) => {
    const question = draft.questions.find((item) => item.id === questionId);
    if (!question) return;
    updateQuestion(questionId, {
      options: (question.options || []).filter((_, i) => i !== optionIndex),
    });
  };

  const effectiveQuestions = getEffectiveFormQuestions(
    { ...draft, id: form?.id },
    sharedQuestions,
    [], // personal info is always collected — hide from builder preview
    allForms.length ? allForms : [{ ...draft, id: form?.id }]
  );
  const includedSharedCount = (sharedQuestions || []).filter(
    (q) => !isSharedExcluded(q.id)
  ).length;

  const questionCount = effectiveQuestions.length;
  const libraryById = useMemo(() => {
    const map = new Map();
    (libraryQuestions || []).forEach((item) => {
      if (item?.id) map.set(item.id, item);
    });
    return map;
  }, [libraryQuestions]);
  const previewQuestions = useMemo(
    () =>
      effectiveQuestions.map((question) =>
        resolvePreviewQuestion(question, libraryById)
      ),
    [effectiveQuestions, libraryById]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        className="tracker-fields-drawer-root"
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          className: "tracker-fields-drawer",
          sx: {
            width: {
              xs: "100% !important",
              sm: "100% !important",
              md: "1100px !important",
            },
            maxWidth: "100vw !important",
            border: "none",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${BORDER}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              Fields — {draft.name || "Untitled form"}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
              Everything saves as you type. The preview on the right is exactly
              what a submitter sees.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              width: { xs: "100%", md: "auto" },
              overflow: "auto",
              p: { xs: 2.5, md: 3.5 },
              bgcolor: "#fff",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Form name"
              value={draft.name}
              onChange={(e) => updateMeta({ name: e.target.value })}
              sx={{ mb: 2.5 }}
            />

            <TextField
              select
              fullWidth
              size="small"
              label="This form is"
              value={draft.role}
              onChange={(e) => updateMeta({ role: e.target.value })}
              sx={{ mb: 1 }}
            >
              {FORM_ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 2.5, lineHeight: 1.5 }}>
              First submission is the sign-up. Follow-up is a review. Daily
              check-in sits outside the journey chain.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Short description"
              placeholder="Optional — shown under the form title"
              value={draft.shortDescription}
              onChange={(e) =>
                updateMeta({ shortDescription: e.target.value })
              }
              sx={{ mb: 4 }}
            />

            {(sharedQuestions || []).length > 0 && (
              <Box sx={{ mb: 4.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.75 }}>
                  On every form
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 2.25, lineHeight: 1.5 }}>
                  Tracker questions included by default. Uncheck to exclude from
                  this form only. Required can differ per form.
                </Typography>
                {(sharedQuestions || []).map((sharedQ) => {
                  const included = !isSharedExcluded(sharedQ.id);
                  const resolved = resolveSharedQuestion(sharedQ);
                  return (
                    <Box
                      key={sharedQ.id}
                      sx={{
                        mb: 2.5,
                        pt: 0.5,
                        opacity: included ? 1 : 0.65,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          // gap: 1.25,
                          flexWrap: "wrap",
                          mb: included ? 1.25 : 0,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={included}
                              onChange={(e) =>
                                setSharedIncluded(sharedQ.id, e.target.checked)
                              }
                              size="small"
                              sx={{
                                color: PRIMARY,
                                "&.Mui-checked": { color: PRIMARY },
                              }}
                            />
                          }
                          // label=""
                          sx={{
                            m: 0,
                            mr: 0.5,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 13,
                              fontWeight: 600,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 120,
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#0f172a",
                          }}
                        >
                          {resolved.label || "Untitled"}
                        </Typography>
                        {/* <Chip
                          size="small"
                          label="Shared"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor: "#fff",
                            border: `1px solid ${BORDER}`,
                          }}
                        /> */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(resolved.required)}
                              disabled={!included}
                              onChange={(e) =>
                                updateSharedOverride(sharedQ.id, {
                                  required: e.target.checked,
                                })
                              }
                              size="small"
                              sx={{
                                color: PRIMARY,
                                "&.Mui-checked": { color: PRIMARY },
                              }}
                            />
                          }
                          label="Required"
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 13,
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </Box>
                      {included && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Placeholder (this form)"
                          value={resolved.placeholder || ""}
                          onChange={(e) =>
                            updateSharedOverride(sharedQ.id, {
                              placeholder: e.target.value,
                            })
                          }
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2.25,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.35 }}>
                  Form-specific questions
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                  Only on this form — in addition to shared questions above.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() => {
                    setLibraryTab("all");
                    setLibrarySearch("");
                    setSelectedLibraryIds([]);
                    setPickLibraryOpen(true);
                  }}
                >
                  From library
                </button>
                <button
                  type="button"
                  className="small-contained-button"
                  onClick={addBlankField}
                >
                  + Add field
                </button>
              </Box>
            </Box>

            {draft.questions.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${BORDER}`,
                  borderRadius: "10px",
                  p: 3.5,
                  textAlign: "center",
                  color: MUTED,
                  fontSize: 14,
                }}
              >
                No form-specific questions yet. Shared ones still show unless
                excluded. Add a field or pick from the library.
              </Box>
            ) : (
              draft.questions.map((question, index) => (
                <Box
                  key={question.id}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    reorderQuestion(dragIndex, index);
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  sx={{
                    mb: 2.25,
                    bgcolor: dragIndex === index ? PRIMARY_SOFT : "transparent",
                    borderRadius: "8px",
                    px: dragIndex === index ? 1 : 0,
                    py: dragIndex === index ? 0.75 : 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      alignItems: "center",
                      gap: 1,
                      overflowX: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        color: MUTED,
                        cursor: "grab",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        "&:active": { cursor: "grabbing" },
                      }}
                      title="Drag to reorder"
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>

                    <TextField
                      size="small"
                      placeholder="Question statement"
                      value={question.label || ""}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          label: e.target.value,
                        })
                      }
                      sx={{ flex: "1 1 200px", minWidth: 160 }}
                    />
                    <TextField
                      select
                      size="small"
                      value={
                        question.measurable
                          ? question.measureType || "custom"
                          : question.fieldType || "short_text"
                      }
                      onChange={(e) => {
                        if (question.measurable) {
                          updateQuestion(question.id, {
                            measureType: e.target.value,
                          });
                          return;
                        }
                        const fieldType = e.target.value;
                        updateQuestion(question.id, {
                          fieldType,
                          options: needsOptions(fieldType)
                            ? question.options?.length
                              ? question.options
                              : ["Option 1", "Option 2"]
                            : [],
                        });
                      }}
                      sx={{ width: 150, flex: "0 0 150px" }}
                    >
                      {question.measurable
                        ? MEASURE_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))
                        : QUESTION_FIELD_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(question.required)}
                          onChange={(e) =>
                            updateQuestion(question.id, {
                              required: e.target.checked,
                            })
                          }
                          size="small"
                          sx={{
                            color: PRIMARY,
                            "&.Mui-checked": { color: PRIMARY },
                          }}
                        />
                      }
                      label="Required"
                      sx={{
                        m: 0,
                        flexShrink: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: 13,
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeQuestion(question.id)}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {needsOptions(question.fieldType) && !question.measurable && (
                    <Box sx={{ mt: 1, pl: 4 }}>
                      {(question.options || []).map((option, optionIndex) => (
                        <Box
                          key={`${question.id}_opt_${optionIndex}`}
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 0.75,
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: MUTED,
                              minWidth: 18,
                            }}
                          >
                            {optionIndex + 1}.
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={option}
                            onChange={(e) =>
                              updateOption(
                                question.id,
                                optionIndex,
                                e.target.value
                              )
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              removeOption(question.id, optionIndex)
                            }
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                      <Typography
                        component="button"
                        type="button"
                        onClick={() => addOption(question.id)}
                        sx={{
                          border: 0,
                          background: "none",
                          p: 0,
                          mt: 0.25,
                          cursor: "pointer",
                          color: PRIMARY,
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        + Add option
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      mt: 0.5,
                      pl: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      component="button"
                      type="button"
                      onClick={() =>
                        setAdvancedOpen((prev) => ({
                          ...prev,
                          [question.id]: !prev[question.id],
                        }))
                      }
                      sx={{
                        border: 0,
                        background: "none",
                        p: 0,
                        cursor: "pointer",
                        color: MUTED,
                        fontSize: 12.5,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        mb: 0.5,
                      }}
                    >
                      Advanced
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          transform: advancedOpen[question.id]
                            ? "rotate(180deg)"
                            : "none",
                          transition: "transform 0.15s ease",
                        }}
                      />
                    </Typography>
                    {question.measurable && (
                      <Chip
                        size="small"
                        label="Leaderboard score"
                        sx={{
                          height: 20,
                          fontWeight: 700,
                          fontSize: 10,
                          bgcolor: PRIMARY_SOFT,
                          color: PRIMARY,
                        }}
                      />
                    )}
                    {question.source === FORM_QUESTION_SOURCES.LIBRARY && (
                      <Chip
                        size="small"
                        icon={
                          <LinkIcon sx={{ fontSize: "13px !important" }} />
                        }
                        label="Library"
                        sx={{
                          height: 20,
                          fontWeight: 700,
                          fontSize: 10,
                          bgcolor: PRIMARY_SOFT,
                          color: PRIMARY,
                        }}
                      />
                    )}
                  </Box>

                  <Collapse in={Boolean(advancedOpen[question.id])}>
                    <Box
                      sx={{
                        mt: 1,
                        ml: 4,
                        p: 1.25,
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                          },
                          gap: 1.25,
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          label="Placeholder"
                          value={question.placeholder || ""}
                          onChange={(e) =>
                            updateQuestion(question.id, {
                              placeholder: e.target.value,
                            })
                          }
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Help text"
                          value={question.helpText || ""}
                          onChange={(e) =>
                            updateQuestion(question.id, {
                              helpText: e.target.value,
                            })
                          }
                          fullWidth
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: 0.75,
                        }}
                      >
                        {[
                          {
                            key: "searchable",
                            label: "Searchable",
                            checked: question.searchable !== false,
                          },
                          {
                            key: "filterable",
                            label: "Filterable",
                            checked: question.filterable !== false,
                          },
                        ].map((option) => (
                          <FormControlLabel
                            key={option.key}
                            control={
                              <Checkbox
                                checked={option.checked}
                                onChange={(e) =>
                                  updateQuestion(question.id, {
                                    [option.key]: e.target.checked,
                                  })
                                }
                                size="small"
                                sx={{
                                  color: PRIMARY,
                                  "&.Mui-checked": { color: PRIMARY },
                                }}
                              />
                            }
                            label={option.label}
                            sx={{
                              mr: 1.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 13,
                                fontWeight: 600,
                              },
                            }}
                          />
                        ))}
                      </Box>

                      <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                        Searchable and filterable are on by default so this
                        field appears in entry search, filters and Excel export.
                        Measurable comes from the Questions library — pick a
                        Measurable question there.
                      </Typography>

                      {question.source === FORM_QUESTION_SOURCES.LIBRARY && (
                        <Typography
                          sx={{ mt: 0.75, fontSize: 11.5, color: MUTED }}
                        >
                          Linked to library id: {question.questionId} ·{" "}
                          {getQuestionModeLabel(question.mode)} ·{" "}
                          {question.measurable
                            ? getMeasureTypeLabel(question.measureType)
                            : getFieldTypeLabel(question.fieldType)}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>

          <Box
            sx={{
              flex: { xs: "0 0 auto", md: "0 0 360px" },
              width: { xs: "100%", md: 360 },
              maxWidth: "100%",
              borderLeft: { md: `1px solid ${BORDER}` },
              borderTop: { xs: `1px solid ${BORDER}`, md: 0 },
              bgcolor: "#f8fafc",
              p: { xs: 2.5, md: 3.5 },
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: 2.5,
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18, color: PRIMARY }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.7,
                  color: MUTED,
                }}
              >
                LIVE PREVIEW
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: "#fff",
                border: `1px solid ${BORDER}`,
                borderRadius: "12px",
                p: 3,
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
                maxWidth: 340,
                mx: "auto",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: PRIMARY,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                {trackerName || "Journey"}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: 22, color: "#0f172a", mb: 0.75 }}
              >
                {draft.name || "Untitled form"}
              </Typography>
              {draft.shortDescription ? (
                <Typography sx={{ fontSize: 13, color: MUTED, mb: 3, lineHeight: 1.45 }}>
                  {draft.shortDescription}
                </Typography>
              ) : (
                <Box sx={{ mb: 2.5 }} />
              )}

              {previewQuestions.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  Add questions to preview the form.
                </Typography>
              ) : (
                previewQuestions.map((question) => (
                  <PreviewField key={question.id} question={question} />
                ))
              )}

              <Box
                sx={{
                  mt: 1.5,
                  bgcolor: PRIMARY,
                  color: "#fff",
                  textAlign: "center",
                  py: 1.35,
                  borderRadius: "8px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                Submit
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {questionCount} question{questionCount === 1 ? "" : "s"}
            {(sharedQuestions || []).length
              ? ` · ${includedSharedCount} shared`
              : ""}
            {draft.questions.length
              ? ` · ${draft.questions.length} form-only`
              : ""}
          </Typography>
          <button
            type="button"
            className="small-contained-button"
            onClick={onClose}
          >
            Done
          </button>
        </Box>
      </Drawer>

      <Dialog
        open={pickLibraryOpen}
        onClose={() => setPickLibraryOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1.25 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
            Add from Questions library
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
            Pick reusable questions. You can still change the statement,
            placeholder and required on this form.
          </Typography>
          <IconButton
            onClick={() => setPickLibraryOpen(false)}
            size="small"
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: { xs: "70vh", sm: 520 },
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${libraryCounts.all} available questions`}
            value={librarySearch}
            onChange={(e) => setLibrarySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {[
              { value: "all", label: `All (${libraryCounts.all})` },
              {
                value: "general",
                label: `General (${libraryCounts.general})`,
              },
              {
                value: "measurable",
                label: `Measurable (${libraryCounts.measurable})`,
              },
            ].map((tab) => {
              const selected = libraryTab === tab.value;
              return (
                <Box
                  key={tab.value}
                  component="button"
                  type="button"
                  onClick={() => setLibraryTab(tab.value)}
                  sx={{
                    border: "1.5px solid",
                    borderColor: selected ? PRIMARY : BORDER,
                    bgcolor: selected ? PRIMARY_SOFT : "#fff",
                    color: selected ? "#0f172a" : MUTED,
                    borderRadius: "999px",
                    px: 1.5,
                    py: 0.7,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: selected ? `0 0 0 1px ${PRIMARY}` : "none",
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              {filteredLibrary.length} shown
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={selectAllLibraryShown}
              disabled={!filteredLibrary.length}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                cursor: filteredLibrary.length ? "pointer" : "default",
                color: PRIMARY,
                fontSize: 13,
                fontWeight: 700,
                opacity: filteredLibrary.length ? 1 : 0.5,
              }}
            >
              Select all shown
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
            {filteredLibrary.length === 0 ? (
              <Typography
                sx={{
                  color: MUTED,
                  fontSize: 14,
                  textAlign: "center",
                  py: 4,
                }}
              >
                {availableLibrary.length === 0
                  ? "No more library questions available for this form."
                  : "No questions match this category or search."}
              </Typography>
            ) : (
              filteredLibrary.map((question) => {
                const checked = selectedLibraryIds.includes(question.id);
                const isMeasurable =
                  question.mode === QUESTION_MODES.MEASURABLE;
                return (
                  <Box
                    key={question.id}
                    onClick={() =>
                      setSelectedLibraryIds((prev) =>
                        prev.includes(question.id)
                          ? prev.filter((id) => id !== question.id)
                          : [...prev, question.id]
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.25,
                      mb: 1,
                      borderRadius: "10px",
                      border: "1.5px solid",
                      borderColor: checked ? PRIMARY : BORDER,
                      bgcolor: checked ? PRIMARY_SOFT : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() =>
                        setSelectedLibraryIds((prev) =>
                          prev.includes(question.id)
                            ? prev.filter((id) => id !== question.id)
                            : [...prev, question.id]
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        mt: -0.25,
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 0.35,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                        >
                          {question.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={isMeasurable ? "Measurable" : "General"}
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: isMeasurable ? PRIMARY_SOFT : "#f1f5f9",
                            color: isMeasurable ? PRIMARY : MUTED,
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        {isMeasurable
                          ? getMeasureTypeLabel(question.measureType)
                          : getFieldTypeLabel(question.fieldType)}
                        {isMeasurable && question.units?.length
                          ? ` · ${question.units.join(", ")}`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {selectedLibraryIds.length} selected
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => {
                setPickLibraryOpen(false);
                setSelectedLibraryIds([]);
                setLibrarySearch("");
                setLibraryTab("all");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="small-contained-button"
              onClick={confirmLibraryPicks}
              disabled={!selectedLibraryIds.length}
            >
              Add selected
            </button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
